// Reconstrói o pill (activity_summary) e o painel (activity_timeline) de uma mensagem
// vinda do histórico salvo (backend/chat/views.py::_build_assistant_message), que só
// carrega `execution_events`/`skill_name` — os dois campos acima só existem hoje em
// memória, gerados ao vivo durante o streaming (ver UnifiedChat.js). Sem isso, o pill
// simplesmente não aparece mais depois que o usuário sai do chat e volta.
//
// `activity_duration_ms` é o tempo total medido pelo PRÓPRIO NAVEGADOR (Date.now(),
// UnifiedChat.js) e persistido via POST /post_activity_duration assim que a execução
// termina (decisão de 2026-09-09: o backend não tenta mais reproduzir esse relógio
// sozinho - variação de rede entre os dois relógios nunca fecha exatamente). Só cai pra
// soma aproximada dos `ms` de cada tool quando a mensagem foi salva antes desse campo
// existir (histórico antigo, sem a persistência) ou quando aquele POST falhou.
function totalDurationMs(events) {
  const soma = events.reduce((acc, evento) => acc + (typeof evento.ms === 'number' ? evento.ms : 0), 0);
  return soma > 0 ? soma : undefined;
}

function resolvedDurationMs(events, persistedDurationMs) {
  return typeof persistedDurationMs === 'number' ? persistedDurationMs : totalDurationMs(events);
}

function buildSkillTimeline(skillName, events, tChat, persistedDurationMs, isError) {
  const duration = resolvedDurationMs(events, persistedDurationMs);
  // Cada chamada de ferramenta grava DOIS eventos brutos com o mesmo `id` (um
  // "running", ao começar, e um "ok"/"error", ao terminar - ver execution_events.py).
  // O fluxo ao vivo (UnifiedChat.js) já resolve isso substituindo a entrada existente
  // pelo `id` a cada novo evento; aqui precisa do mesmo agrupamento, senão cada
  // ferramenta vira duas linhas no painel reconstruído do histórico.
  const eventoFinalPorId = new Map();
  events.forEach(evento => {
    eventoFinalPorId.set(evento.id, evento);
  });

  const tools = Array.from(eventoFinalPorId.values()).map(evento => ({
    tool_id: evento.id,
    // Mesma chave/fallback do fluxo ao vivo (UnifiedChat.js) - sem isso, o histórico
    // recarregado mostrava o nome técnico da tool (evento.name), não a tradução.
    tool_label: tChat(`executionTrail.tools.${evento.name}`, { defaultValue: evento.label || evento.name }),
    // Achado 2026-09-10 (ponto 4): uma tool cujo último evento gravado é "running"
    // (nunca chegou o "ok"/"error" de fechamento, porque a execução INTEIRA morreu no
    // meio dela) não pode virar 'completed' só por não ser literalmente 'error' - ela
    // nunca terminou de verdade. Se a mensagem como um todo falhou (isError), qualquer
    // tool sem confirmação de sucesso ('ok') também é 'error'.
    status: evento.status === 'error' || (isError && evento.status !== 'ok') ? 'error' : 'completed',
    durationMs: typeof evento.ms === 'number' ? evento.ms : undefined,
  }));

  // Achado 2026-09-10: reconstruído do histórico, o wrapper "skill" e o "Concluído"
  // sintético vinham sempre 'completed', mesmo quando a mensagem tinha de fato falhado
  // (msg.status/msg.error) - a trilha reconstruída mentia "deu tudo certo" numa execução
  // que na verdade errou. Sem `done` quando erra, igual ao comportamento ao vivo (que só
  // adiciona o "Concluído" sintético no fechamento de SUCESSO, nunca no de erro).
  return {
    activity_summary: { skill_name: skillName, duration_ms: duration },
    activity_timeline: isError
      ? [{ type: 'skill', status: 'error', data: { skill_name: skillName, tools }, durationMs: duration }]
      : [
          { type: 'skill', status: 'completed', data: { skill_name: skillName, tools }, durationMs: duration },
          { type: 'done', status: 'completed', data: {} },
        ],
  };
}

// FR-015 (revisado 2026-09-09): tool_event sem skill_name (agente principal do chat) -
// cada chamada vira sua PRÓPRIA linha (type 'tool'), mesmo formato de data.tools de uma
// skill, em vez de agrupar por tipo de ferramenta (writing/analyzing/preparing_file) -
// agrupar escondia quantas vezes o agente rodou create_document/executar_codigo/etc.
// Mesma dedup por `id` de buildSkillTimeline acima (cada chamada grava dois eventos
// brutos, "running" e "ok"/"error", com o mesmo id).
function buildMainAgentTimeline(events, persistedDurationMs, tChat, isError) {
  const stepsPorChave = new Map();
  const ordem = [];

  events.forEach(evento => {
    if (evento.name === 'web_search_citation') {
      // Citações de busca continuam agrupadas num ÚNICO step "searching" (lista de
      // sites) - diferente das demais tools, aqui o agrupamento é o comportamento
      // desejado, não uma limitação. `url` (Tarefa 1, FR-002) é o campo dedicado que
      // sobrevive à persistência - `output` é removido de propósito antes de gravar.
      if (!stepsPorChave.has('searching')) {
        ordem.push('searching');
      }
      const previous = stepsPorChave.get('searching');
      const prevSites = previous?.data?.sites || [];
      let sites = prevSites;
      if (!prevSites.some(site => site.url === evento.url)) {
        let domain = evento.url || '';
        try {
          domain = new URL(evento.url).hostname;
        } catch {
          // URL inesperada - mantém o valor cru como domain.
        }
        sites = [...prevSites, { domain, url: evento.url, title: evento.label }];
      }
      stepsPorChave.set('searching', {
        type: 'searching',
        status: 'completed',
        data: { sites, site_count: sites.length },
      });
      return;
    }

    if (!stepsPorChave.has(evento.id)) {
      ordem.push(evento.id);
    }
    stepsPorChave.set(evento.id, {
      type: 'tool',
      // Mesmo achado do buildSkillTimeline acima: tool sem 'ok' de fechamento, numa
      // mensagem que falhou como um todo, também é 'error' - não "completed" por
      // omissão.
      status: evento.status === 'error' || (isError && evento.status !== 'ok') ? 'error' : 'completed',
      data: {
        tool_id: evento.id,
        // Mesma chave/fallback do fluxo ao vivo (UnifiedChat.js) - sem isso, o histórico
        // recarregado mostrava o nome técnico da tool (evento.name), não a tradução.
        tool_label: tChat(`executionTrail.tools.${evento.name}`, { defaultValue: evento.label || evento.name }),
      },
      durationMs: typeof evento.ms === 'number' ? evento.ms : undefined,
    });
  });

  // Mesmo achado de buildSkillTimeline acima: sem "Concluído" sintético quando a
  // mensagem de fato errou - reconstrução fiel ao que aconteceu, não sempre "sucesso".
  return {
    activity_summary: { skill_name: null, duration_ms: resolvedDurationMs(events, persistedDurationMs) },
    activity_timeline: isError
      ? ordem.map(chave => stepsPorChave.get(chave))
      : [...ordem.map(chave => stepsPorChave.get(chave)), { type: 'done', status: 'completed', data: {} }],
  };
}

// Enriquece cada mensagem do histórico recarregado com activity_summary/activity_timeline
// quando ela tem execution_events mas ainda não tem esses campos (mensagem ao vivo já os
// carrega, gerados no próprio streaming - não mexe nesse caso).
export function enrichHistoryWithActivity(messages, tChat) {
  if (!Array.isArray(messages)) {
    return messages;
  }

  return messages.map(msg => {
    // `msg.status === 'running'`: achado 2026-09-14 (specs/front_planning_tools/plan.md) -
    // sem essa checagem, uma execução que ainda está rodando de verdade no servidor ganhava um
    // activity_summary SINTÉTICO marcado "Concluído" só por já ter execution_events parciais.
    // Esse resumo falso nunca era removido depois, e a regra de render (Message/index.js) é
    // "mostra indicador OU resumo, nunca os dois" - o resumo falso sempre ganhava do indicador
    // ao vivo que o polling (post_execution_progress) tenta desenhar logo em seguida. Pulando
    // aqui, a mensagem fica sem indicador só até o poll (dispara imediato, não espera os 5s do
    // intervalo) responder com o progresso real.
    if (!msg || msg.role !== 'assistant' || msg.activity_summary || msg.status === 'running' || !Array.isArray(msg.execution_events) || msg.execution_events.length === 0) {
      return msg;
    }

    // `msg.status` vem de backend/execution_progress/helpers.py::resolver_status -
    // "error"/"interrupted" são os dois jeitos de uma execução ter parado sem sucesso
    // (falha explícita vs. processo morto no meio, ambos com msg.error preenchido pelo
    // backend/chat/views.py::_build_assistant_message quando aplicável).
    const isError = msg.status === 'error' || msg.status === 'interrupted' || Boolean(msg.error);
    const derived = msg.skill_name
      ? buildSkillTimeline(msg.skill_name, msg.execution_events, tChat, msg.activity_duration_ms, isError)
      : buildMainAgentTimeline(msg.execution_events, msg.activity_duration_ms, tChat, isError);

    return { ...msg, ...derived };
  });
}
